import {Inject, Injectable} from '@angular/core';
import {BackendErrorCodeEnum} from '@application/helper/backend-error-code.enum';
import {OrderLinesGridService} from '@application/helper/orderbook/order-lines-grid.service';
import {renameProductionSchedulePathsToNewPositionInColoredYarnSets} from '@application/helper/production-schedule-builder';
import {updateProductionSchedulePathsOfColoredYarnSets} from '@application/helper/production-schedule-builder/update-production-schedule-paths-of-colored-yarn-sets';
import {getListDrawingFromProductionScheduleItem} from '@application/helper/production-schedule/get-list-drawing-from-production-schedule-item';
import {DimensionsInPx} from '@domain/dimensions-in-px';
import {BmpProductionScheduleItemInPathGroup} from '@domain/production-schedule/bmp-production-schedule-item-in-path-group';
import {Drawing} from '@domain/production-schedule/drawing';
import {DrawingRecolorInformation} from '@domain/production-schedule/drawing-recolor-information';
import {FinishingForProductionSchedule} from '@domain/production-schedule/finishing-for-production-schedule';
import {PathLabelDrawing} from '@domain/production-schedule/path-label-drawing';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionScheduleOrderLine} from '@domain/production-schedule/production-schedule-order-line';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {ProductionSchedulePhase} from '@domain/production-schedule/production-schedule-phase.enum';
import {TargetForListOfDrawingsEnum} from '@domain/target-for-list-of-drawings.enum';
import {CreelMap} from '@domain/textile-data/creel/creel-map';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {PathWidth} from '@domain/textile-data/machine-quality/path-width';
import {DRAWINGS, Drawings} from '@infrastructure/http/drawing/drawings';
import {PRODUCTION_ORDERS, ProductionOrders} from '@infrastructure/http/production-order/production-orders';
import {PRODUCTION_SCHEDULES, ProductionSchedules} from '@infrastructure/http/production-schedule/production-schedules';
import {ListDrawing} from '@presentation/components/drawing-list/list-drawing';
import {ListDrawingConfiguration} from '@presentation/components/drawing-list/list-drawing-configuration';
import {ListDrawingOrderLine} from '@presentation/components/drawing-list/list-drawing-order-line';
import {AddDrawingToColoredYarnSetCommand} from '@presentation/pages/texfab/production-schedule/add/plan/add-drawing-to-colored-yarn-set-command';
import {AddDrawingToProductionSchedulePathCommand} from '@presentation/pages/texfab/production-schedule/add/plan/add-drawing-to-production-schedule-path-command';
import {Zone} from '@presentation/pages/texfab/production-schedule/add/plan/builder/zone';
import {EditProductionScheduleItemCommand} from '@presentation/pages/texfab/production-schedule/add/plan/edit-production-schedule-item-command';
import {LoadingState} from '@presentation/pages/texfab/production-schedule/add/plan/loading-state';
import {MoveProductionScheduleItemToColoredYarnSetCommand} from '@presentation/pages/texfab/production-schedule/add/plan/move-production-schedule-item-to-colored-yarn-set-command';
import {MoveProductionScheduleItemToProductionSchedulePathCommand} from '@presentation/pages/texfab/production-schedule/add/plan/move-production-schedule-item-to-production-schedule-path-command';
import {MoveProductionSchedulePathToColoredYarnSetCommand} from '@presentation/pages/texfab/production-schedule/add/plan/move-production-schedule-path-to-colored-yarn-set-command';
import {ParametersForAddingDrawingToColoredYarnSet} from '@presentation/pages/texfab/production-schedule/add/plan/parameters-for-adding-drawing-to-colored-yarn-set';
import {ParametersForAddingDrawingToProductionSchedulePath} from '@presentation/pages/texfab/production-schedule/add/plan/parameters-for-adding-drawing-to-production-schedule-path';
import {ParametersForEditingProductionScheduleItem} from '@presentation/pages/texfab/production-schedule/add/plan/parameters-for-editing-production-schedule-item';
import {ParametersForMovingProductionScheduleItemToColoredYarnSet} from '@presentation/pages/texfab/production-schedule/add/plan/parameters-for-moving-production-schedule-item-to-colored-yarn-set';
import {ParametersForMovingProductionScheduleItemToProductionSchedulePath} from '@presentation/pages/texfab/production-schedule/add/plan/parameters-for-moving-production-schedule-item-to-production-schedule-path';
import {ParametersForMovingProductionSchedulePathToColoredYarnSet} from '@presentation/pages/texfab/production-schedule/add/plan/parameters-for-moving-production-schedule-path-to-colored-yarn-set';
import {ParametersForRemovingProductionScheduleItem} from '@presentation/pages/texfab/production-schedule/add/plan/parameters-for-removing-production-schedule-item';
import {ParametersForRemovingProductionSchedulePath} from '@presentation/pages/texfab/production-schedule/add/plan/parameters-for-removing-production-schedule-path';
import {ParametersForUpdatingDrawingsAndOrderLinesOfProductionSchedule} from '@presentation/pages/texfab/production-schedule/add/plan/parameters-for-updating-drawings-and-order-lines-of-production-schedule';
import {CachedPathLabelDrawingReference, ProductionSchedulePathLabelDrawingsCache} from '@presentation/pages/texfab/production-schedule/add/plan/production-schedule-path-label-drawings-cache';
import {ProductionSchedulePlan} from '@presentation/pages/texfab/production-schedule/add/plan/production-schedule-plan';
import {RemoveProductionScheduleItemCommand} from '@presentation/pages/texfab/production-schedule/add/plan/remove-production-schedule-item-command';
import {RemoveProductionSchedulePathCommand} from '@presentation/pages/texfab/production-schedule/add/plan/remove-production-schedule-path-command';
import {UpdateDrawingsAndOrderLinesOfProductionScheduleCommand} from '@presentation/pages/texfab/production-schedule/add/plan/update-drawings-and-order-lines-of-production-schedule-command';
import {AssertionUtils, BackendError, BaseComponent, DialogBuilderFactoryService, DialogType, LocalStorageService, ToastService, TranslateService} from '@vdw/angular-component-library';
import {cloneDeep, each, find, first, includes, isEmpty, isEqual, isNil, map, min, noop, pull, reduce, remove, size, some} from 'lodash-es';
import {BehaviorSubject, Observable, Observer, Subject} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';
import {RecolorService} from '../drawing-library/recolor.service';
import {ParametersForRemovingProductionScheduleItems} from './parameters-for-removing-production-schedule-items';
import {RemoveProductionScheduleItemsCommand} from './remove-production-schedule-items-command';

interface QueuedProductionSchedulePlanAction {
  isProcessing: boolean;
  operation: string;
  parameters: any;
  postExecute: (productionSchedule: ProductionSchedule) => ProductionSchedule;
  onSuccess?: () => void;
}

interface QueuedProductionScheduleItemRecolor {
  parameters: {
    productionScheduleItem: ProductionScheduleItemInPathGroup;
    coloredYarnSetWithStartDent: OverviewListColoredYarnSetWithStartDent;
  };
  isRecoloring: boolean;

  onProductionScheduleItemRecolored(): void;

  onProductionScheduleItemRecoloredFailed(error: BackendError): void;
}

@Injectable()
export class ProductionSchedulePlanService extends BaseComponent implements ProductionSchedulePlan {
  private static readonly addDrawingToColoredYarnSetOperation: string = 'addDrawingToColoredYarnSet';
  private static readonly addDrawingToProductionSchedulePathOperation: string = 'addDrawingToProductionSchedulePath';
  private static readonly removeProductionScheduleItemOperation: string = 'removeProductionScheduleItem';
  private static readonly removeProductionScheduleItemsOperation: string = 'removeProductionScheduleItems';
  private static readonly removeProductionSchedulePathOperation: string = 'removeProductionSchedulePath';
  private static readonly editProductionScheduleItemOperation: string = 'editProductionScheduleItem';
  private static readonly moveProductionScheduleItemToProductionSchedulePathOperation: string = 'moveProductionScheduleItemToProductionSchedulePath';
  private static readonly moveProductionScheduleItemToColoredYarnSetOperation: string = 'moveProductionScheduleItemToColoredYarnSet';
  private static readonly moveProductionSchedulePathToColoredYarnSetOperation: string = 'moveProductionSchedulePathToColoredYarnSet';
  private static readonly updateDrawingsAndOrderLinesOfProductionScheduleOperation: string = 'updateDrawingsAndOrderLinesOfProductionScheduleOperation';

  public readonly currentUpdatedProductionSchedule: Observable<ProductionSchedule>;
  public readonly currentEditingPaths: Observable<boolean>;
  public readonly didPatchProductionSchedulePlan: Observable<void>;
  public readonly currentSelectedZone: Observable<Zone>;

  private readonly productionSchedulePathLabelDrawingsCache = new ProductionSchedulePathLabelDrawingsCache();
  private productionSchedule: ProductionSchedule;
  private readonly updatedProductionScheduleSubject = new Subject<ProductionSchedule>();
  private readonly editingPathsSubject = new BehaviorSubject<boolean>(false);
  private readonly patchProductionSchedulePlanSubject = new Subject<void>();
  private initializeProductionScheduleSubject: Subject<void>;
  private readonly selectedZoneSubject = new Subject<Zone>();
  private readonly addDrawingToColoredYarnSetCommand = new AddDrawingToColoredYarnSetCommand();
  private readonly addDrawingToProductionSchedulePathCommand = new AddDrawingToProductionSchedulePathCommand();
  private readonly removeProductionScheduleItemCommand = new RemoveProductionScheduleItemCommand();
  private readonly removeProductionScheduleItemsCommand = new RemoveProductionScheduleItemsCommand();
  private readonly removeProductionSchedulePathCommand = new RemoveProductionSchedulePathCommand();
  private readonly editProductionScheduleItemCommand = new EditProductionScheduleItemCommand();
  private readonly moveProductionScheduleItemToProductionSchedulePathCommand = new MoveProductionScheduleItemToProductionSchedulePathCommand();
  private readonly moveProductionScheduleItemToColoredYarnSetCommand = new MoveProductionScheduleItemToColoredYarnSetCommand();
  private readonly moveProductionSchedulePathToColoredYarnSetCommand = new MoveProductionSchedulePathToColoredYarnSetCommand();
  private readonly updateDrawingsAndOrderLinesOfProductionScheduleCommand = new UpdateDrawingsAndOrderLinesOfProductionScheduleCommand();
  private hasChangesThatAreNotSaved: boolean = false;
  private factorForConversionFromMillimetersToDents: number;
  private queuedProductionSchedulePlanActions: QueuedProductionSchedulePlanAction[] = [];
  private queuedProductionScheduleItemsToRecolor: QueuedProductionScheduleItemRecolor[] = [];
  private definedProductionSchedulePlanActions = {
    addDrawingToColoredYarnSet: (parameters: ParametersForAddingDrawingToColoredYarnSet): ProductionSchedule => this.addDrawingToColoredYarnSetCommand.execute(parameters),
    addDrawingToProductionSchedulePath: (parameters: ParametersForAddingDrawingToProductionSchedulePath): ProductionSchedule => this.addDrawingToProductionSchedulePathCommand.execute(parameters),
    removeProductionScheduleItem: (parameters: ParametersForRemovingProductionScheduleItem): ProductionSchedule => this.removeProductionScheduleItemCommand.execute(parameters),
    removeProductionScheduleItems: (parameters: ParametersForRemovingProductionScheduleItems): ProductionSchedule => this.removeProductionScheduleItemsCommand.execute(parameters),
    removeProductionSchedulePath: (parameters: ParametersForRemovingProductionSchedulePath): ProductionSchedule => this.removeProductionSchedulePathCommand.execute(parameters),
    editProductionScheduleItem: (parameters: ParametersForEditingProductionScheduleItem): ProductionSchedule => this.editProductionScheduleItemCommand.execute(parameters),
    moveProductionScheduleItemToProductionSchedulePath: (parameters: ParametersForMovingProductionScheduleItemToProductionSchedulePath): ProductionSchedule =>
      this.moveProductionScheduleItemToProductionSchedulePathCommand.execute(parameters),
    moveProductionScheduleItemToColoredYarnSet: (parameters: ParametersForMovingProductionScheduleItemToColoredYarnSet): ProductionSchedule =>
      this.moveProductionScheduleItemToColoredYarnSetCommand.execute(parameters),
    moveProductionSchedulePathToColoredYarnSet: (parameters: ParametersForMovingProductionSchedulePathToColoredYarnSet): ProductionSchedule =>
      this.moveProductionSchedulePathToColoredYarnSetCommand.execute(parameters),
    updateDrawingsAndOrderLinesOfProductionScheduleOperation: (parameters: ParametersForUpdatingDrawingsAndOrderLinesOfProductionSchedule): ProductionSchedule =>
      this.updateDrawingsAndOrderLinesOfProductionScheduleCommand.execute(parameters)
  };

  private cancelOngoingRecoloringSubject: Subject<void> = new Subject<void>();
  private readonly operationsThatRequireProductionScheduleItemsToBeRecolored = [
    ProductionSchedulePlanService.addDrawingToColoredYarnSetOperation,
    ProductionSchedulePlanService.addDrawingToProductionSchedulePathOperation,
    ProductionSchedulePlanService.moveProductionScheduleItemToColoredYarnSetOperation,
    ProductionSchedulePlanService.moveProductionScheduleItemToProductionSchedulePathOperation,
    ProductionSchedulePlanService.moveProductionSchedulePathToColoredYarnSetOperation
  ];

  public constructor(
    @Inject(PRODUCTION_SCHEDULES) private readonly productionSchedules: ProductionSchedules,
    @Inject(PRODUCTION_ORDERS) private readonly productionOrders: ProductionOrders,
    @Inject(DRAWINGS) private readonly drawings: Drawings,
    private readonly translate: TranslateService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly localStorageService: LocalStorageService,
    private readonly toastService: ToastService,
    private readonly recolorService: RecolorService,
    private readonly orderlinesGridService: OrderLinesGridService
  ) {
    super();
    this.currentUpdatedProductionSchedule = this.updatedProductionScheduleSubject.asObservable();
    this.currentEditingPaths = this.editingPathsSubject.asObservable();
    this.didPatchProductionSchedulePlan = this.patchProductionSchedulePlanSubject.asObservable();
    this.currentSelectedZone = this.selectedZoneSubject.asObservable();
  }

  public applyMeasurementsCalculationsFactor(factorForConversionFromMillimetersToDents: number): void {
    this.factorForConversionFromMillimetersToDents = factorForConversionFromMillimetersToDents;
  }

  public initialiseProductionSchedule(productionSchedule: ProductionSchedule): Observable<void> {
    this.productionSchedule = cloneDeep(productionSchedule);
    this.discardUnsavedChanges();
    this.initializeProductionScheduleSubject = new Subject<void>();

    this.recolorProductionScheduleIfNeeded(productionSchedule);

    this.dispatchNewProductionSchedule(this.productionSchedule);

    return this.initializeProductionScheduleSubject.asObservable();
  }

  public updateProductionScheduleAfterDecreation(productionSchedule: ProductionSchedule): void {
    this.productionSchedule = productionSchedule;
  }

  public hasUnsavedChanges(newBuilder: boolean): boolean {
    if (newBuilder) {
      return !isEmpty(this.queuedProductionSchedulePlanActions) || this.hasChangesThatAreNotSaved;
    } else {
      return !isEmpty(this.queuedProductionSchedulePlanActions);
    }
  }

  public discardUnsavedChanges(): void {
    this.queuedProductionSchedulePlanActions = [];
  }

  public discardTemporarySavedChanges(): void {
    if (!AssertionUtils.isNullOrUndefined(this.productionSchedule?.id)) {
      this.productionSchedules.cancelTemporarySavedChanges(this.productionSchedule.id.toString()).pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe();
    }
  }

  public dispatchNewProductionSchedule(productionSchedule: ProductionSchedule): void {
    this.updatedProductionScheduleSubject.next(productionSchedule);
  }

  public addDrawingToColoredYarnSet(listDrawing: ListDrawing, startDent: number, indexInColoredYarnSet?: number): Observable<ProductionSchedulePath> {
    let productionSchedulePath = new Subject<ProductionSchedulePath>();

    this.selectDrawingOrOrderLineIfNotAlreadySelected(listDrawing);

    this.queuedProductionSchedulePlanActions.push({
      isProcessing: false,
      operation: ProductionSchedulePlanService.addDrawingToColoredYarnSetOperation,
      parameters: {
        listDrawing,
        startDentOfColoredYarnSet: startDent,
        indexInColoredYarnSet,
        dpi: this.localStorageService.get('dpi')
      } as ParametersForAddingDrawingToColoredYarnSet,
      postExecute: (productionSchedule: ProductionSchedule) => {
        setTimeout(() => {
          productionSchedulePath.next(
            productionSchedule.productionSchedulePathsOfColoredYarnSets
              .find((productionSchedulePathsOfColoredYarnSets: ProductionSchedulePathsOfColoredYarnSet) => {
                return productionSchedulePathsOfColoredYarnSets.productionScheduleColoredYarnSet.startDent === startDent;
              })
              .productionSchedulePaths.at(-1)
          );
        });

        return renameProductionSchedulePathsToNewPositionInColoredYarnSets(productionSchedule);
      }
    });

    this.dispatchNewProductionSchedule(this.getProductionScheduleWithAllRemainingActionsApplied());
    this.processFirstItemInQueuedProductionSchedulePlanActions();

    return productionSchedulePath.asObservable();
  }

  public addDrawingToProductionSchedulePath(
    listDrawing: ListDrawing,
    productionSchedulePathNumber: number,
    productionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent,
    indexInProductionSchedulePath?: number
  ): Observable<ProductionSchedulePath> {
    let productionSchedulePath = new Subject<ProductionSchedulePath>();

    this.selectDrawingOrOrderLineIfNotAlreadySelected(listDrawing);

    this.queuedProductionSchedulePlanActions.push({
      isProcessing: false,
      operation: ProductionSchedulePlanService.addDrawingToProductionSchedulePathOperation,
      parameters: {
        listDrawing,
        productionSchedulePathNumber,
        startDentOfColoredYarnSet: productionScheduleColoredYarnSet.startDent,
        indexInProductionSchedulePath
      } as ParametersForAddingDrawingToProductionSchedulePath,
      postExecute: (productionSchedule: ProductionSchedule) => {
        setTimeout(() => {
          productionSchedule.productionSchedulePathsOfColoredYarnSets.forEach((productionSchedulePathsOfColoredYarnSets: ProductionSchedulePathsOfColoredYarnSet) => {
            productionSchedulePathsOfColoredYarnSets.productionSchedulePaths.forEach((path: ProductionSchedulePath) => {
              if (path.pathNumber === productionSchedulePathNumber) {
                productionSchedulePath.next(path);
              }
            });
          });
        });

        return renameProductionSchedulePathsToNewPositionInColoredYarnSets(productionSchedule);
      }
    });

    this.dispatchNewProductionSchedule(this.getProductionScheduleWithAllRemainingActionsApplied());
    this.processFirstItemInQueuedProductionSchedulePlanActions();

    return productionSchedulePath.asObservable();
  }

  public manualSaveProductionSchedule(id?: string, productionSchedulePhase: ProductionSchedulePhase = null): void {
    this.disableUnsavedChanges();

    this.productionSchedules
      .verifyProductionSchedule(!AssertionUtils.isNullOrUndefined(id) ? id : this.productionSchedule.id.toString(), true, productionSchedulePhase)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((productionSchedule: ProductionSchedule) => this.showInfoToastAndContinue(productionSchedule));
  }

  public addPathLayoutTemplates(productionSchedule: ProductionSchedule): void {
    this.productionSchedules
      .setProductionSchedulePlan(productionSchedule.id as string, cloneDeep(productionSchedule.productionSchedulePathsOfColoredYarnSets), this.factorForConversionFromMillimetersToDents)
      .subscribe((patchedProductionSchedule: ProductionSchedule) => {
        productionSchedule.warnings = patchedProductionSchedule.warnings;
        productionSchedule.errors = patchedProductionSchedule.errors;
        productionSchedule.updateOrderLineQuantities();
        productionSchedule.updatePathLengths(patchedProductionSchedule);

        this.productionSchedule = productionSchedule;

        this.cleanUpAndHandleNextItemInQueue();
      });

    this.dispatchNewProductionSchedule(this.getProductionScheduleWithAllRemainingActionsApplied());
  }

  public hasNoPaths(productionSchedule: ProductionSchedule): boolean {
    return productionSchedule?.productionSchedulePathsOfColoredYarnSets?.every((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => {
      return productionSchedulePathsOfColoredYarnSet?.productionSchedulePaths.length === 0;
    });
  }

  public removeProductionScheduleItem(productionScheduleItemInPathGroupUuid: string, productionSchedulePathNumber: number, startDentOfColoredYarnSet: number, newBuilder?: boolean): void {
    this.queuedProductionSchedulePlanActions.push({
      isProcessing: false,
      operation: ProductionSchedulePlanService.removeProductionScheduleItemOperation,
      parameters: {
        productionScheduleItemInPathGroupUuid,
        productionSchedulePathNumber,
        startDentOfColoredYarnSet,
        newBuilder
      } as ParametersForRemovingProductionScheduleItem,
      postExecute: (productionSchedule: ProductionSchedule) => renameProductionSchedulePathsToNewPositionInColoredYarnSets(productionSchedule)
    });

    this.dispatchNewProductionSchedule(this.getProductionScheduleWithAllRemainingActionsApplied());
    this.processFirstItemInQueuedProductionSchedulePlanActions();
  }

  public removeProductionScheduleItems(productionScheduleItems: ProductionScheduleItemInPathGroup[], onSuccess: () => void = (): void => null): void {
    this.queuedProductionSchedulePlanActions.push({
      isProcessing: false,
      operation: ProductionSchedulePlanService.removeProductionScheduleItemsOperation,
      parameters: {
        productionScheduleItems
      } as ParametersForRemovingProductionScheduleItems,
      postExecute: (productionSchedule: ProductionSchedule) => renameProductionSchedulePathsToNewPositionInColoredYarnSets(productionSchedule),
      onSuccess
    });

    this.dispatchNewProductionSchedule(this.getProductionScheduleWithAllRemainingActionsApplied());
    this.processFirstItemInQueuedProductionSchedulePlanActions();
  }

  public lookupCommercialWidthInPathWidths(commercialWidthInMM: number, pathWidths: PathWidth[]): number | null {
    for (const pathWidth of pathWidths) {
      if (Math.abs(pathWidth.commercialInCM * 10 - commercialWidthInMM) < 0.5) {
        return pathWidth.actualDents;
      }
    }
    return null;
  }

  public removeProductionSchedulePath(productionSchedulePathNumber: number, startDentOfColoredYarnSet: number): void {
    this.queuedProductionSchedulePlanActions.push({
      isProcessing: false,
      operation: ProductionSchedulePlanService.removeProductionSchedulePathOperation,
      parameters: {
        productionSchedulePathNumber,
        startDentOfColoredYarnSet
      } as ParametersForRemovingProductionSchedulePath,
      postExecute: (productionSchedule: ProductionSchedule) => renameProductionSchedulePathsToNewPositionInColoredYarnSets(productionSchedule)
    });

    this.dispatchNewProductionSchedule(this.getProductionScheduleWithAllRemainingActionsApplied());
    this.processFirstItemInQueuedProductionSchedulePlanActions();
  }

  public editProductionScheduleItem(
    productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup,
    productionScheduleItemInPathGroupIndex: number,
    productionSchedulePathIndex: number,
    startDentOfColoredYarnSet: number
  ): void {
    this.queuedProductionSchedulePlanActions.push({
      isProcessing: false,
      operation: ProductionSchedulePlanService.editProductionScheduleItemOperation,
      parameters: {
        productionScheduleItemInPathGroup,
        productionScheduleItemInPathGroupIndex,
        productionSchedulePathIndex,
        startDentOfColoredYarnSet
      },
      postExecute: (productionSchedule: ProductionSchedule) => renameProductionSchedulePathsToNewPositionInColoredYarnSets(productionSchedule)
    });

    this.dispatchNewProductionSchedule(this.getProductionScheduleWithAllRemainingActionsApplied());
    this.processFirstItemInQueuedProductionSchedulePlanActions();
  }

  public moveProductionScheduleItemToProductionSchedulePath(
    sourceProductionSchedulePathNumber: number,
    indexInSourceProductionSchedulePath: number,
    sourceProductionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent,
    destinationProductionSchedulePathNumber: number,
    indexInDestinationProductionSchedulePath: number,
    destinationProductionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent,
    productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup
  ): void {
    if (
      this.shouldMoveProductionScheduleItemToProductionSchedulePath(
        sourceProductionSchedulePathNumber,
        destinationProductionSchedulePathNumber,
        indexInSourceProductionSchedulePath,
        indexInDestinationProductionSchedulePath
      )
    ) {
      this.queuedProductionSchedulePlanActions.push({
        isProcessing: false,
        operation: ProductionSchedulePlanService.moveProductionScheduleItemToProductionSchedulePathOperation,
        parameters: {
          sourceProductionSchedulePathNumber,
          startDentOfSourceColoredYarnSet: sourceProductionScheduleColoredYarnSet.startDent,
          destinationProductionSchedulePathNumber,
          indexInDestinationProductionSchedulePath,
          startDentOfDestinationColoredYarnSet: destinationProductionScheduleColoredYarnSet.startDent,
          productionScheduleItemInPathGroup
        } as ParametersForMovingProductionScheduleItemToProductionSchedulePath,
        postExecute: (productionSchedule: ProductionSchedule) => renameProductionSchedulePathsToNewPositionInColoredYarnSets(productionSchedule)
      });

      this.dispatchNewProductionSchedule(this.getProductionScheduleWithAllRemainingActionsApplied());
      this.processFirstItemInQueuedProductionSchedulePlanActions();
    }
  }

  public disableUnsavedChanges(): void {
    this.hasChangesThatAreNotSaved = false;
  }

  public moveProductionScheduleItemToColoredYarnSet(
    sourceProductionSchedulePathNumber: number,
    sourceProductionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent,
    destinationProductionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent,
    productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup,
    indexInColoredYarnSet?: number
  ): void {
    this.queuedProductionSchedulePlanActions.push({
      isProcessing: false,
      operation: ProductionSchedulePlanService.moveProductionScheduleItemToColoredYarnSetOperation,
      parameters: {
        sourceProductionSchedulePathNumber,
        startDentOfSourceColoredYarnSet: sourceProductionScheduleColoredYarnSet.startDent,
        startDentOfDestinationColoredYarnSet: destinationProductionScheduleColoredYarnSet.startDent,
        productionScheduleItemInPathGroup,
        dpi: this.localStorageService.get('dpi'),
        indexInColoredYarnSet
      } as ParametersForMovingProductionScheduleItemToColoredYarnSet,
      postExecute: (productionSchedule: ProductionSchedule) => renameProductionSchedulePathsToNewPositionInColoredYarnSets(productionSchedule)
    });

    this.dispatchNewProductionSchedule(this.getProductionScheduleWithAllRemainingActionsApplied());
    this.processFirstItemInQueuedProductionSchedulePlanActions();
  }

  public moveProductionSchedulePathToColoredYarnSet(
    sourceProductionSchedulePath: ProductionSchedulePath,
    sourceProductionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent,
    destinationProductionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent,
    indexInProductionSchedulePathsOfColoredYarnSet?: number,
    pathIndex?: number
  ): void {
    const productionScheduleItemsOfProductionSchedulePath: ProductionScheduleItemInPathGroup[] = sourceProductionSchedulePath.getUniqueProductionScheduleItems();

    const listDrawings: ListDrawing[] = map(productionScheduleItemsOfProductionSchedulePath, (productionScheduleItem: ProductionScheduleItemInPathGroup) =>
      getListDrawingFromProductionScheduleItem(productionScheduleItem, this.productionSchedule)
    );

    this.queuedProductionSchedulePlanActions.push({
      isProcessing: false,
      operation: ProductionSchedulePlanService.moveProductionSchedulePathToColoredYarnSetOperation,
      parameters: {
        listDrawings,
        sourceProductionSchedulePathNumber: sourceProductionSchedulePath.pathNumber,
        startDentOfSourceColoredYarnSet: sourceProductionScheduleColoredYarnSet.startDent,
        startDentOfDestinationColoredYarnSet: destinationProductionScheduleColoredYarnSet.startDent,
        indexInProductionSchedulePathsOfColoredYarnSet,
        pathIndex
      } as ParametersForMovingProductionSchedulePathToColoredYarnSet,
      postExecute: (productionSchedule: ProductionSchedule) => renameProductionSchedulePathsToNewPositionInColoredYarnSets(productionSchedule)
    });

    this.dispatchNewProductionSchedule(this.getProductionScheduleWithAllRemainingActionsApplied());
    this.processFirstItemInQueuedProductionSchedulePlanActions();
  }

  public updateDrawingsAndOrderLinesOfProductionSchedule(drawings: Drawing[], orderLines: ProductionScheduleOrderLine[], currentProductionSchedule?: ProductionSchedule): void {
    if (!AssertionUtils.isNullOrUndefined(currentProductionSchedule)) {
      this.productionSchedule = currentProductionSchedule;
    }

    this.queuedProductionSchedulePlanActions.push({
      isProcessing: false,
      operation: ProductionSchedulePlanService.updateDrawingsAndOrderLinesOfProductionScheduleOperation,
      parameters: {
        productionSchedule: this.productionSchedule,
        drawings,
        orderLines
      } as ParametersForUpdatingDrawingsAndOrderLinesOfProductionSchedule,
      postExecute: (productionSchedule: ProductionSchedule) => productionSchedule
    });

    this.dispatchNewProductionSchedule(this.getProductionScheduleWithAllRemainingActionsApplied());
    this.processFirstItemInQueuedProductionSchedulePlanActions();
  }

  public updateEditingPaths(isEditingPaths: boolean): void {
    this.editingPathsSubject.next(isEditingPaths);
  }

  public setSelectedZone(zone: Zone): void {
    this.selectedZoneSubject.next(zone);
  }

  public canVerify(): boolean {
    return !this.hasUnsavedChanges(false) && this.productionSchedule?.hasAtLeastOneProductionScheduleItemPerColoredYarnSet();
  }

  public cancelOngoingRecoloring(): void {
    this.cancelOngoingRecoloringSubject.next();
  }

  public setProductionSchedule(productionSchedule: ProductionSchedule): void {
    this.productionSchedule = cloneDeep(productionSchedule);
  }

  private shouldMoveProductionScheduleItemToProductionSchedulePath(
    sourceProductionSchedulePathNumber: number,
    destinationProductionSchedulePathNumber: number,
    indexInSourceProductionSchedulePath: number,
    indexInDestinationProductionSchedulePath: number
  ): boolean {
    const productionSchedule = this.getProductionScheduleWithAllRemainingActionsApplied();
    const sourceProductionSchedulePath = productionSchedule.getProductionSchedulePath(sourceProductionSchedulePathNumber);

    const isInSamePath: boolean = isEqual(sourceProductionSchedulePathNumber, destinationProductionSchedulePathNumber);
    const isOnSamePositionOfSamePath: boolean = isEqual(indexInSourceProductionSchedulePath, indexInDestinationProductionSchedulePath);
    const hasOnlyOneProductionScheduleItemInSamePath: boolean = size(sourceProductionSchedulePath.pathGroups) === 1;
    return !(isInSamePath && (isOnSamePositionOfSamePath || hasOnlyOneProductionScheduleItemInSamePath));
  }

  private recolorProductionScheduleItem(
    productionScheduleItem: ProductionScheduleItemInPathGroup,
    coloredYarnSetWithStartDent: OverviewListColoredYarnSetWithStartDent,
    onProductionScheduleItemRecolored: () => void,
    onProductionScheduleItemRecoloredFailed: (error: BackendError) => void
  ): void {
    const {id: imageId} = productionScheduleItem.drawing.image;
    const currentRatio = productionScheduleItem.drawing.dimensionsInPixels.heightInPx / productionScheduleItem.drawing.dimensionsInPixels.widthInPx;
    const {coloredYarnSet, position} = coloredYarnSetWithStartDent;

    let outputHeight = min([productionScheduleItem.drawing.dimensionsInPixels.heightInPx, 400]);
    let outputWidth = outputHeight / currentRatio;
    if (outputWidth > 300) {
      outputWidth = 300;
      outputHeight = outputWidth * currentRatio;
    }

    const outputDimensionsInPx: DimensionsInPx = {
      heightInPx: Math.round(outputHeight),
      widthInPx: Math.round(outputWidth)
    };

    const mappingForCurrentPositionOnMachine = find(this.productionSchedule.mappingForCurrentPositionOnMachine, (creelMap: CreelMap) => {
      return isEqual(creelMap.coloredYarnSetId, coloredYarnSet.id);
    });

    const orderLineId = productionScheduleItem.orderLineId;
    const finishingId = productionScheduleItem instanceof BmpProductionScheduleItemInPathGroup && !isNil(productionScheduleItem.finishing) ? productionScheduleItem.finishing.id : null;

    if (productionScheduleItem.drawing.missingDesign) {
      productionScheduleItem.recolorDrawing({
        imageData: 'assets/images/missing-design.svg',
        mainColorPosition: 1,
        borderColorPosition: 1,
        commercialDimensionsInMM: productionScheduleItem.commercialDimensionsInMM,
        finishing: null,
        mappingForDesign: null
      });
      this.cleanUpAndRecolorNextProductionScheduleItemInQueue();
      onProductionScheduleItemRecolored();
    } else {
      this.drawings
        .getRecoloredImageConfiguration(
          imageId,
          productionScheduleItem.machineQuality?.id ?? this.productionSchedule.machineQuality.id,
          coloredYarnSet.overviewListColorSet.id,
          coloredYarnSet.id,
          mappingForCurrentPositionOnMachine,
          TargetForListOfDrawingsEnum.PRODUCTION_ORDER,
          {input: productionScheduleItem.drawing.dimensionsInPixels, output: outputDimensionsInPx},
          orderLineId,
          position,
          finishingId
        )
        .pipe(
          finalize(() => this.cleanUpAndRecolorNextProductionScheduleItemInQueue()),
          takeUntil(this.cancelOngoingRecoloringSubject)
        )
        .subscribe({
          next: (drawingRecolorInformation: DrawingRecolorInformation) => {
            productionScheduleItem.recolorDrawing({
              imageData: drawingRecolorInformation.image,
              mainColorPosition: drawingRecolorInformation.mainColorsCreelPosition.position,
              borderColorPosition: drawingRecolorInformation.borderColorsCreelPosition.position,
              commercialDimensionsInMM: drawingRecolorInformation.commercialDimensionsInMm,
              finishing: !isNil(drawingRecolorInformation.finishing) ? FinishingForProductionSchedule.fromOverviewListFinishing(drawingRecolorInformation.finishing) : null,
              mappingForDesign: !isNil(drawingRecolorInformation.mappingForDesign) ? drawingRecolorInformation.mappingForDesign : null
            });

            onProductionScheduleItemRecolored();
          },
          error: (error: BackendError) => onProductionScheduleItemRecoloredFailed(error)
        });
    }
  }

  private loadPathLabelIfNeeded(
    drawing: PathLabelDrawing,
    coloredYarnSetWithStartDent: OverviewListColoredYarnSetWithStartDent,
    onPathLabelLoadedWhenNotCachedYet: (cachedPathLabelDrawingReference: CachedPathLabelDrawingReference) => void,
    onPathLabelAlreadyCached: (cachedPathLabelDrawingReference: CachedPathLabelDrawingReference) => void,
    onPathLabelLoadedFailed: (error: BackendError) => void
  ): void {
    if (!this.productionSchedulePathLabelDrawingsCache.hasPathLabelDrawingBeenLoaded(drawing, coloredYarnSetWithStartDent)) {
      const {id: imageId} = drawing.image;
      const coloredYarnSet = coloredYarnSetWithStartDent.coloredYarnSet;
      const mappingForCurrentPositionOnMachine = find(this.productionSchedule.mappingForCurrentPositionOnMachine, (creelMap: CreelMap) => {
        return isEqual(creelMap.coloredYarnSetId, coloredYarnSet.id);
      });

      const cachedPathLabelDrawingReference = {
        drawing,
        state: LoadingState.BUSY
      };
      this.productionSchedulePathLabelDrawingsCache.add(cachedPathLabelDrawingReference, coloredYarnSetWithStartDent);

      this.drawings
        .getRecoloredPathLabelImage(
          imageId,
          this.productionSchedule.machineQuality.id,
          coloredYarnSet.overviewListColorSet.id,
          mappingForCurrentPositionOnMachine,
          drawing.dimensionsInPixels,
          coloredYarnSetWithStartDent.position,
          this.productionSchedule.creel.name,
          this.productionSchedule.machine.id,
          this.productionSchedule.initialFreeZones.pattern.id
        )
        .pipe(takeUntil(this.cancelOngoingRecoloringSubject))
        .subscribe({
          next: (result: string) => {
            cachedPathLabelDrawingReference.drawing.image.data = result;
            cachedPathLabelDrawingReference.state = LoadingState.DONE;

            this.productionSchedulePathLabelDrawingsCache.add(cachedPathLabelDrawingReference, coloredYarnSetWithStartDent);

            onPathLabelLoadedWhenNotCachedYet(cachedPathLabelDrawingReference);
          },
          error: (error: BackendError) => onPathLabelLoadedFailed(error)
        });
    } else {
      onPathLabelAlreadyCached(this.productionSchedulePathLabelDrawingsCache.getPathLabelDrawingCachedReference(drawing, coloredYarnSetWithStartDent));
    }
  }

  private processFirstItemInQueuedProductionSchedulePlanActions(): void {
    const firstItemInQueue: QueuedProductionSchedulePlanAction = first(this.queuedProductionSchedulePlanActions);

    if (!isNil(firstItemInQueue) && !firstItemInQueue.isProcessing) {
      firstItemInQueue.isProcessing = true;

      const parametersWithProductionSchedule = firstItemInQueue.parameters;
      parametersWithProductionSchedule['productionSchedule'] = cloneDeep(this.productionSchedule);

      let productionScheduleToAdjust = this.definedProductionSchedulePlanActions[firstItemInQueue.operation](parametersWithProductionSchedule);

      productionScheduleToAdjust = firstItemInQueue.postExecute(productionScheduleToAdjust);

      if (isEqual(firstItemInQueue.operation, ProductionSchedulePlanService.updateDrawingsAndOrderLinesOfProductionScheduleOperation)) {
        this.saveDesignsAndOrderLines(productionScheduleToAdjust);
      } else if (includes(this.operationsThatRequireProductionScheduleItemsToBeRecolored, firstItemInQueue.operation)) {
        this.recolorAllProductionScheduleItemsThatAreNotRecolored(productionScheduleToAdjust).subscribe({
          next: () => this.setProductionSchedulePlan(productionScheduleToAdjust, firstItemInQueue.onSuccess),
          error: () => {
            this.orderlinesGridService.setIsFillingPath(false);
            this.recolorService.setRecolorErrorCatch();
            this.cleanUpAndHandleNextItemInQueue();
          }
        });
      } else {
        this.setProductionSchedulePlan(productionScheduleToAdjust, firstItemInQueue.onSuccess);
      }
    }
  }

  private setProductionSchedulePlan(productionSchedule: ProductionSchedule, onSuccess: () => void = noop): void {
    productionSchedule = updateProductionSchedulePathsOfColoredYarnSets(productionSchedule);
    this.hasChangesThatAreNotSaved = true;

    if (this.productionSchedule.completingPartially) {
      this.productionOrders
        .updateProductionOrder(
          productionSchedule.id as string,
          cloneDeep(productionSchedule.productionSchedulePathsOfColoredYarnSets),
          this.factorForConversionFromMillimetersToDents,
          this.productionSchedule.status
        )
        .subscribe(() => {
          productionSchedule.updateOrderLineQuantities();
          this.productionSchedule = productionSchedule;

          this.cleanUpAndHandleNextItemInQueue();
          onSuccess();
        });
    } else {
      productionSchedule.productionSchedulePathsOfColoredYarnSets.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => {
        productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.technicalWidthInDents = Math.round(
          productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.technicalWidthInDents
        );
        productionSchedulePathsOfColoredYarnSet.productionSchedulePaths.forEach((productionSchedulePath: ProductionSchedulePath) => {
          productionSchedulePath.technicalWidthInDents = Math.round(productionSchedulePath.technicalWidthInDents);
          productionSchedulePath.pathGroups.forEach((pathGroup: ProductionScheduleItemInPathGroup) => {
            pathGroup.technicalDimensions.widthInDents = Math.round(pathGroup.technicalDimensions.widthInDents);
          });
        });
      });

      this.productionSchedules
        .setProductionSchedulePlan(productionSchedule.id as string, cloneDeep(productionSchedule.productionSchedulePathsOfColoredYarnSets), this.factorForConversionFromMillimetersToDents)
        .subscribe((patchedProductionSchedule: ProductionSchedule) => {
          productionSchedule.warnings = patchedProductionSchedule.warnings;
          productionSchedule.errors = patchedProductionSchedule.errors;
          productionSchedule.updateOrderLineQuantities();
          productionSchedule.updatePathLengths(patchedProductionSchedule);
          productionSchedule.buggy = patchedProductionSchedule.buggy;

          this.productionSchedule = productionSchedule;

          this.cleanUpAndHandleNextItemInQueue();
          onSuccess();
        });
    }

    if (this.queuedProductionSchedulePlanActions.length === 1) {
      this.orderlinesGridService.setIsFillingPath(false);
    }
  }

  private saveDesignsAndOrderLines(productionSchedule: ProductionSchedule): void {
    this.productionSchedules.saveDesignsAndOrderLines(productionSchedule.id, productionSchedule.designs, productionSchedule.orderLines, productionSchedule.name).subscribe(() => {
      productionSchedule.updateOrderLineQuantities();
      this.productionSchedule = productionSchedule;

      this.cleanUpAndHandleNextItemInQueue();
    });
  }

  private getProductionScheduleWithAllRemainingActionsApplied(): ProductionSchedule {
    return reduce(
      this.queuedProductionSchedulePlanActions,
      (adjustedProductionSchedule: ProductionSchedule, itemInQueueToProcess: QueuedProductionSchedulePlanAction) => {
        const parametersWithProductionSchedule = itemInQueueToProcess.parameters;
        parametersWithProductionSchedule['productionSchedule'] = adjustedProductionSchedule;

        return this.definedProductionSchedulePlanActions[itemInQueueToProcess.operation](parametersWithProductionSchedule);
      },
      cloneDeep(this.productionSchedule)
    );
  }

  private recolorProductionScheduleIfNeeded(productionSchedule: ProductionSchedule): void {
    const productionScheduleItemsWithMissingAlternativeMachineQualityOrColoredYarnSet = [];

    productionSchedule
      .getNonRecoloredProductionScheduleItemsPerColoredYarnSet()
      .forEach((nonRecoloredProductionScheduleItems: ProductionScheduleItemInPathGroup[], coloredYarnSetWithStartDent: OverviewListColoredYarnSetWithStartDent) => {
        each(nonRecoloredProductionScheduleItems, (nonRecoloredProductionScheduleItem: ProductionScheduleItemInPathGroup) => {
          const onProductionScheduleItemRecolored = (): void => {
            this.productionSchedule = productionSchedule;

            if (isEqual(productionSchedule.getAmountOfNonRecoloredProductionScheduleItems() - size(productionScheduleItemsWithMissingAlternativeMachineQualityOrColoredYarnSet), 0)) {
              this.removeProductionScheduleItemsWithMissingAlternativeMachineQualityOrColoredYarnSet(productionScheduleItemsWithMissingAlternativeMachineQualityOrColoredYarnSet);
              this.dispatchNewProductionSchedule(this.getProductionScheduleWithAllRemainingActionsApplied());
            }
          };

          const onProductionScheduleItemRecoloredFailed = (error: BackendError): void => {
            if (!isEqual(BackendErrorCodeEnum[error.errorContext.errorCode], BackendErrorCodeEnum.ALTERNATIVES)) {
              this.initializeProductionScheduleSubject.error(error);
            } else {
              productionScheduleItemsWithMissingAlternativeMachineQualityOrColoredYarnSet.push(nonRecoloredProductionScheduleItem);

              if (isEqual(productionSchedule.getAmountOfNonRecoloredProductionScheduleItems() - size(productionScheduleItemsWithMissingAlternativeMachineQualityOrColoredYarnSet), 0)) {
                this.removeProductionScheduleItemsWithMissingAlternativeMachineQualityOrColoredYarnSet(productionScheduleItemsWithMissingAlternativeMachineQualityOrColoredYarnSet);
              }
            }
          };

          this.recolorProductionScheduleItem(nonRecoloredProductionScheduleItem, coloredYarnSetWithStartDent, onProductionScheduleItemRecolored, onProductionScheduleItemRecoloredFailed);
        });
      });

    productionSchedule.getUniquePathLabelDrawingsPerColoredYarnSet().forEach((pathLabelDrawings: PathLabelDrawing[], coloredYarnSetWithStartDent: OverviewListColoredYarnSetWithStartDent) => {
      each(pathLabelDrawings, (pathLabelDrawing: PathLabelDrawing) => {
        const onPathLabelLoadedWhenNotCachedYet = (cachedPathLabelDrawingReference: CachedPathLabelDrawingReference): void => {
          this.updateLoadedPathLabelDrawingInProductionSchedule(productionSchedule, cachedPathLabelDrawingReference);
        };

        const onPathLabelAlreadyCached = onPathLabelLoadedWhenNotCachedYet;

        const onPathLabelLoadedFailed = (error: BackendError): void => {
          this.initializeProductionScheduleSubject.error(error);

          this.productionSchedulePathLabelDrawingsCache.clearNonLoadedPathLabelDrawings();
        };

        this.loadPathLabelIfNeeded(pathLabelDrawing, coloredYarnSetWithStartDent, onPathLabelLoadedWhenNotCachedYet, onPathLabelAlreadyCached, onPathLabelLoadedFailed);
      });
    });
  }

  private updateLoadedPathLabelDrawingInProductionSchedule(productionSchedule: ProductionSchedule, cachedPathLabelDrawingReference: CachedPathLabelDrawingReference): void {
    productionSchedule.updateLoadedPathLabelDrawing(cachedPathLabelDrawingReference.drawing);

    this.productionSchedule = productionSchedule;
    this.dispatchNewProductionSchedule(this.getProductionScheduleWithAllRemainingActionsApplied());
  }

  private showInfoToastAndContinue(productionSchedule: ProductionSchedule): void {
    this.toastService.showInfo({
      tapToDismiss: false,
      timeOut: 2000,
      message: this.translate.instant('GENERAL.ACTIONS.UPDATED_OBJECT', {
        object: this.translate.instant('PRODUCTION_ORDER.PRODUCTION_ORDER', {count: 1}),
        name: productionSchedule.name
      })
    });
    this.patchProductionSchedulePlanSubject.next();
  }

  private cleanUpAndHandleNextItemInQueue(): void {
    pull(this.queuedProductionSchedulePlanActions, find(this.queuedProductionSchedulePlanActions, ['isProcessing', true]));

    this.dispatchNewProductionSchedule(this.getProductionScheduleWithAllRemainingActionsApplied());
    this.processFirstItemInQueuedProductionSchedulePlanActions();
  }

  private processFirstItemInQueuedProductionScheduleItemsToRecolor(): void {
    const firstItemInQueue: QueuedProductionScheduleItemRecolor = first(this.queuedProductionScheduleItemsToRecolor);
    if (!isNil(firstItemInQueue) && !firstItemInQueue.isRecoloring) {
      firstItemInQueue.isRecoloring = true;

      const {productionScheduleItem, coloredYarnSetWithStartDent} = firstItemInQueue.parameters;
      const {onProductionScheduleItemRecolored, onProductionScheduleItemRecoloredFailed} = firstItemInQueue;

      this.recolorProductionScheduleItem(productionScheduleItem, coloredYarnSetWithStartDent, onProductionScheduleItemRecolored, onProductionScheduleItemRecoloredFailed);
    }
  }

  private cleanUpAndRecolorNextProductionScheduleItemInQueue(): void {
    remove(this.queuedProductionScheduleItemsToRecolor, {isRecoloring: true});

    this.processFirstItemInQueuedProductionScheduleItemsToRecolor();
  }

  private addProductionScheduleItemToProductionScheduleItemRecolorQueue(
    productionScheduleItem: ProductionScheduleItemInPathGroup,
    coloredYarnSetWithStartDent: OverviewListColoredYarnSetWithStartDent,
    onProductionScheduleItemRecolored: () => void,
    onProductionScheduleItemRecoloredFailed: (error: BackendError) => void
  ): void {
    const productionScheduleItemRecolorQueueItem = {
      parameters: {
        productionScheduleItem,
        coloredYarnSetWithStartDent
      },
      isRecoloring: false,
      onProductionScheduleItemRecolored,
      onProductionScheduleItemRecoloredFailed
    };

    this.queuedProductionScheduleItemsToRecolor.push(productionScheduleItemRecolorQueueItem);

    this.processFirstItemInQueuedProductionScheduleItemsToRecolor();
  }

  private showAlertDialog(errorMessage: string): void {
    this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
      titleText: 'PRODUCTION_ORDER.WEAVE_PLAN.WEAVE_PLAN',
      messageText: errorMessage,
      type: DialogType.INFORMATION
    });
  }

  private removeProductionScheduleItemsWithMissingAlternativeMachineQualityOrColoredYarnSet(productionScheduleItems: ProductionScheduleItemInPathGroup[]): void {
    if (!isEmpty(productionScheduleItems)) {
      this.showAlertDialog(this.translate.instant('GENERAL.ERRORS.BACKEND.ALTERNATIVES'));
      this.removeProductionScheduleItems(productionScheduleItems, noop);
      this.processFirstItemInQueuedProductionSchedulePlanActions();
    }
  }

  private selectDrawingOrOrderLineIfNotAlreadySelected(listDrawing: ListDrawing): void {
    if (listDrawing instanceof ListDrawingConfiguration) {
      const isDrawingAlreadySelected = some(this.productionSchedule.designs, {id: listDrawing.drawing.id});

      if (!isDrawingAlreadySelected) {
        this.updateDrawingsAndOrderLinesOfProductionSchedule([...this.productionSchedule.designs, cloneDeep(listDrawing.drawing)], this.productionSchedule.orderLines);
      }
    } else if (listDrawing instanceof ListDrawingOrderLine) {
      const isOrderLineAlreadySelected = some(this.productionSchedule.orderLines, {id: listDrawing.orderLine.id});

      if (!isOrderLineAlreadySelected) {
        this.updateDrawingsAndOrderLinesOfProductionSchedule(this.productionSchedule.designs, [...this.productionSchedule.orderLines, cloneDeep(listDrawing.orderLine)]);
      }
    }
  }

  private recolorAllProductionScheduleItemsThatAreNotRecolored(productionSchedule: ProductionSchedule): Observable<void> {
    return new Observable((observer: Observer<void>) => {
      if (isEqual(productionSchedule.getAmountOfNonRecoloredProductionScheduleItems(), 0)) {
        observer.next();
        observer.complete();
      } else {
        productionSchedule
          .getNonRecoloredProductionScheduleItemsPerColoredYarnSet()
          .forEach((nonRecoloredProductionScheduleItems: ProductionScheduleItemInPathGroup[], coloredYarnSetWithStartDent: OverviewListColoredYarnSetWithStartDent) => {
            each(nonRecoloredProductionScheduleItems, (nonRecoloredProductionScheduleItem: ProductionScheduleItemInPathGroup) => {
              const onProductionScheduleItemRecolored = (): void => {
                if (isEqual(productionSchedule.getAmountOfNonRecoloredProductionScheduleItems(), 0)) {
                  observer.next();
                  observer.complete();
                }
              };

              const onProductionScheduleItemRecoloredFailed = (error: BackendError): void => {
                const errorMessage = isNil(error.message) ? 'PRODUCTION_ORDER.WEAVE_PLAN.WARNINGS.COULD_NOT_RECOLOR' : error.message;
                this.showAlertDialog(errorMessage);
                this.queuedProductionScheduleItemsToRecolor = [];

                observer.error(errorMessage);
                observer.complete();
              };

              this.addProductionScheduleItemToProductionScheduleItemRecolorQueue(
                nonRecoloredProductionScheduleItem,
                coloredYarnSetWithStartDent,
                onProductionScheduleItemRecolored,
                onProductionScheduleItemRecoloredFailed
              );
            });
          });
      }
    });
  }
}
